import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const empty = {
  name: "", cpf: "", cnpj: "", rg: "", birth_date: "", email: "", phone: "",
  address: "", location: "", marital_status: "", role: "", department: "", hire_date: "", salary: 0, notes: "", status: "ativo",
  branch_id: "",
  jornada_mensal: 220, jornada_diaria: 8,
  bank_name: "", bank_agency: "", bank_account: "", pix_key: "",
  work_days_pattern: 0, off_days_pattern: 0, default_shift: "", schedule_start_date: "",
};

export default function EmployeeFormDialog({ open, onOpenChange, onSubmit, initial, title = "Colaborador" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (open) {
      const base = { ...empty, ...(initial || {}) };
      // Default branch_id for non-admin = user's own branch
      if (!base.branch_id && user?.branch_id) base.branch_id = user.branch_id;
      setForm(base);
      api.get("/branches").then(({ data }) => setBranches(data)).catch(() => setBranches([]));
    }
  }, [open, initial, user]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isAdmin && !form.branch_id) {
      alert("Selecione a filial do colaborador");
      return;
    }
    setLoading(true);
    try {
      await onSubmit({
        ...form,
        salary: parseFloat(form.salary) || 0,
        jornada_mensal: parseInt(form.jornada_mensal) || 220,
        jornada_diaria: parseInt(form.jornada_diaria) || 8,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">{title}</DialogTitle>
          <DialogDescription>Preencha os dados pessoais e profissionais.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="name">Nome completo *</Label>
              <Input id="name" data-testid="emp-name-input" value={form.name} onChange={(e) => upd("name", e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cpf">CPF</Label>
              <Input id="cpf" data-testid="emp-cpf-input" value={form.cpf} onChange={(e) => upd("cpf", e.target.value)} placeholder="000.000.000-00" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cnpj">CNPJ (se PJ)</Label>
              <Input id="cnpj" data-testid="emp-cnpj-input" value={form.cnpj} onChange={(e) => upd("cnpj", e.target.value)} placeholder="00.000.000/0000-00" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rg">RG</Label>
              <Input id="rg" value={form.rg} onChange={(e) => upd("rg", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="birth_date">Data de nascimento</Label>
              <Input id="birth_date" type="date" value={form.birth_date} onChange={(e) => upd("birth_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Telefone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="(00) 00000-0000" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="address">Endereço</Label>
              <Input id="address" value={form.address} onChange={(e) => upd("address", e.target.value)} />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="location">Localidade (opcional)</Label>
              <Input id="location" data-testid="emp-location-input" value={form.location} onChange={(e) => upd("location", e.target.value)} placeholder="Ex: Araguari/MG, Obra Centro, Filial Norte" />
              <p className="text-[11px] text-slate-500">Cidade, região ou obra do colaborador. Usada para filtrar o relatório de pagamentos.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="marital_status">Estado Civil</Label>
              <Select value={form.marital_status || ""} onValueChange={(v) => upd("marital_status", v === "__none__" ? "" : v)}>
                <SelectTrigger id="marital_status" data-testid="emp-marital-status-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Não informado</SelectItem>
                  <SelectItem value="Solteiro(a)">Solteiro(a)</SelectItem>
                  <SelectItem value="Casado(a)">Casado(a)</SelectItem>
                  <SelectItem value="União Estável">União Estável</SelectItem>
                  <SelectItem value="Divorciado(a)">Divorciado(a)</SelectItem>
                  <SelectItem value="Viúvo(a)">Viúvo(a)</SelectItem>
                  <SelectItem value="Separado(a)">Separado(a)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h4 className="font-medium text-sm mb-3">Dados Profissionais</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="role">Cargo</Label>
                <Input id="role" data-testid="emp-role-input" value={form.role} onChange={(e) => upd("role", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="department">Departamento</Label>
                <Input id="department" value={form.department} onChange={(e) => upd("department", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hire_date">Data de admissão</Label>
                <Input id="hire_date" type="date" value={form.hire_date} onChange={(e) => upd("hire_date", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="salary">Salário (R$)</Label>
                <Input id="salary" type="number" step="0.01" value={form.salary} onChange={(e) => upd("salary", e.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="status">Status</Label>
                <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                  <SelectTrigger data-testid="emp-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="branch">Filial *</Label>
                  <Select value={form.branch_id || ""} onValueChange={(v) => upd("branch_id", v)}>
                    <SelectTrigger data-testid="emp-branch-select"><SelectValue placeholder="Escolha a filial..." /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(() => {
                const selectedBranch = branches.find((b) => b.id === form.branch_id);
                const isCLT = (selectedBranch?.name || "").trim().toUpperCase() === "CELETISTAS";
                if (!isCLT) return null;
                return (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="jornada_mensal">Jornada Mensal (h)</Label>
                      <Input id="jornada_mensal" type="number" min="1" step="1" value={form.jornada_mensal ?? 220} onChange={(e) => upd("jornada_mensal", parseInt(e.target.value || "0") || 0)} data-testid="emp-jornada-mensal" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="jornada_diaria">Jornada Contratual (h/dia)</Label>
                      <Input id="jornada_diaria" type="number" min="1" step="1" value={form.jornada_diaria ?? 8} onChange={(e) => upd("jornada_diaria", parseInt(e.target.value || "0") || 0)} data-testid="emp-jornada-diaria" />
                      <div className="text-[11px] text-slate-500">Horas por dia previstas em convenção coletiva — base para cálculo de horas extras.</div>
                    </div>
                  </>
                );
              })()}
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="notes">Observações</Label>
                <Textarea id="notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} rows={3} />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h4 className="font-medium text-sm mb-3">Dados Bancários</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="bank_name">Banco</Label>
                <Input id="bank_name" data-testid="emp-bank-name" value={form.bank_name} onChange={(e) => upd("bank_name", e.target.value)} placeholder="Ex: Itaú, Bradesco, Nubank" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bank_agency">Agência</Label>
                <Input id="bank_agency" data-testid="emp-bank-agency" value={form.bank_agency} onChange={(e) => upd("bank_agency", e.target.value)} placeholder="0000" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bank_account">Conta</Label>
                <Input id="bank_account" data-testid="emp-bank-account" value={form.bank_account} onChange={(e) => upd("bank_account", e.target.value)} placeholder="00000-0" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pix_key">Chave PIX</Label>
                <Input id="pix_key" data-testid="emp-pix-key" value={form.pix_key} onChange={(e) => upd("pix_key", e.target.value)} placeholder="CPF, email, telefone ou chave aleatória" />
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-sm mb-3">Escala Padrão (Painel de Escalas)</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="work_days_pattern">Dias de Trabalho</Label>
                <Input id="work_days_pattern" type="number" min="0" max="30" data-testid="emp-work-days" value={form.work_days_pattern} onChange={(e) => upd("work_days_pattern", parseInt(e.target.value || "0", 10))} placeholder="Ex: 6" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="off_days_pattern">Dias de Folga</Label>
                <Input id="off_days_pattern" type="number" min="0" max="30" data-testid="emp-off-days" value={form.off_days_pattern} onChange={(e) => upd("off_days_pattern", parseInt(e.target.value || "0", 10))} placeholder="Ex: 1" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="default_shift">Turno Padrão</Label>
                <Select value={form.default_shift || ""} onValueChange={(v) => upd("default_shift", v === "__none__" ? "" : v)}>
                  <SelectTrigger id="default_shift" data-testid="emp-default-shift"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Não definido</SelectItem>
                    <SelectItem value="manha">Manhã</SelectItem>
                    <SelectItem value="tarde">Tarde</SelectItem>
                    <SelectItem value="noite">Noite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="schedule_start_date">Início da Escala</Label>
                <Input id="schedule_start_date" type="date" data-testid="emp-schedule-start" value={form.schedule_start_date} onChange={(e) => upd("schedule_start_date", e.target.value)} />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Ex.: 6 dias de trabalho + 1 de folga a partir de 01/01. Deixe zero para desabilitar o padrão automático.</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="emp-form-submit"
              className="bg-brand-900 hover:bg-brand-800 text-white"
            >
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
